import React, { useEffect, useState } from 'react'
import * as R from 'ramda'
import styled from 'styled-components'
import {
  Link,
  Redirect,
  Route,
  Switch,
  useLocation,
  useParams,
  useRouteMatch,
} from 'react-router-dom'
import constants from 'config/constants'
import { Menu, Segment } from 'semantic-ui-react'

import { SurveyDeliveryMethodInputContainer } from 'components/SurveyDeliveryMethodInput/container'
import EmailTemplateSettings from 'components/EmailTemplateSettings'
import useAxios, { cache } from 'hooks/useAxios'
import { Loader } from 'shared/components/Loader'
import { EmbedSurveyV2 } from 'organisms/EmbedSurveyV2'
import { DEFAULT_EMBED_SURVEY_SETUPS } from 'organisms/EmbedSurveyV2/utils'
import ErrorBoundary from 'components/ErrorBoundary'
import { ErrorMessage } from 'components/ErrorMessage'
import SendingHistoryContainer from 'components/SendingHistory/container'
import OneoffEmailDeliverySettingContainer from 'components/OneoffEmailDelevirySetting/container'
import EventBasedEmailDeliverySettingContainer from 'components/EventBassedEmailDeliverySetting/container'
import SendAnotherBatch from 'components/SendAnotherBatch'
import OngoingEmailDeliverySettingContainer from 'organisms/OngoingEmailDeliverySetting'
import {
  transformResponseToPresentationalState,
  findMetricQuestion,
} from 'organisms/SurveySettingsV2/utils'
import IntercomSurveyPublish from 'components/IntercomSurveyPublish'
import { useDynamicConstants } from 'config/dynamicConstants'
import TicketIntegrationConfiguration from 'components/TicketIntegrationConfigurations'
import WebEmbedSurveyPublish from 'components/WebEmbedSurveyPublish'

export const SurveyPublishSettingContext = React.createContext()

const MenuContainer = styled.div`
  background: white;
  border-bottom: 1px solid #e5e5e5;

  .ui.secondary.pointing.menu {
    border: 0;
  }
  .ui.secondary.pointing.menu .item {
    padding: 18px 20px;
  }
  .ui.secondary.pointing.menu .active.item {
    border-bottom: 3px solid #53b573;
  }
`

export default function SurveyPublishSettingV2() {
  const surveyId = useSurveyId()
  const { data: survey } = useAxios({
    url: `account/survey/${surveyId}/`,
  })

  const { data: surveyBuilder, fetch } = useAxios({
    url: `dashboard/survey-builder/v2/${R.prop('token', survey)}/`,
    skip: true,
  })
  const { data: languages } = useAxios({ url: 'survey/languages/' })
  const match = useRouteMatch()
  const location = useLocation()
  const isEmailTemplateSettingTab = location.pathname.endsWith('emails/')

  useEffect(() => {
    if (!surveyBuilder && survey) {
      fetch()
    }
  }, [fetch, survey, surveyBuilder])

  const surveyToken = R.prop('token', survey)
  useEffect(() => {
    return () => {
      if (!surveyToken) {
        return
      }
      const emailCacheKeyPrefix = `survey/survey/${surveyToken}/email-`
      cache.keys().forEach((cacheKey) => {
        if (cacheKey.includes(emailCacheKeyPrefix)) {
          cache.delete(cacheKey)
        }
      })
    }
  }, [surveyToken])

  const [genericEmbedPlaceHoldersEmbedCode, setGenericEmbedPlaceHoldersEmbedCode] = useState({
    teamMemberEmail: '',
    teamMemberId: '',
    teamMemberFirstName: '',
    teamMemberLastName: '',
    customerEmail: '',
    customerId: '',
    customerFirstName: '',
    customerLastName: '',
    companyName: '',
    ticketId: '',
    ticketSubject: '',
    ticketGroup: '',
  })
  const [embedSurveySetups, setEmbedSurveySetups] = useState({ ...DEFAULT_EMBED_SURVEY_SETUPS })

  useEffect(() => {
    if (survey) {
      setGenericEmbedPlaceHoldersEmbedCode((placeholders) => {
        return {
          ...placeholders,
          ...survey.settings.placeholders,
          ticketGroup: R.prop('group', survey.settings.ticketCustomFields),
        }
      })

      if (!R.isNil(R.path(['settings', 'setups'], survey))) {
        setEmbedSurveySetups(() => {
          return {
            ...survey.settings.setups,
          }
        })
      }
    }
  }, [survey])

  const dynamicConstants = useDynamicConstants()

  if (!survey || !surveyBuilder || !languages) {
    return <Loader withContainer />
  }

  if (survey.type === 'n/a') {
    return <SurveyDeliveryMethodInputContainer />
  }

  if (constants.emailSurveyTypes.includes(survey.type)) {
    let emailTypeName = ''
    if (survey.type === 'event_based_email') {
      emailTypeName = 'Event-based email'
    } else if (survey.type === 'oneoff_email') {
      emailTypeName = 'One-off email'
    } else {
      emailTypeName = 'On-going'
    }

    return (
      <div>
        <h2>{emailTypeName}</h2>
        <MenuContainer className="mb4">
          <div className="flex">
            <Menu pointing secondary>
              <Link to={`${match.url}/emails/`}>
                <Menu.Item active={isEmailTemplateSettingTab}>
                  <i className="far fa-envelope mr2" />
                  Emails
                </Menu.Item>
              </Link>
              <Link to={`${match.url}/delivery/`}>
                <Menu.Item active={!isEmailTemplateSettingTab}>
                  <i className="far fa-paper-plane mr2" />
                  Delivery
                </Menu.Item>
              </Link>
            </Menu>
          </div>
        </MenuContainer>
        <Switch>
          <Route path={`${match.path}emails/`}>
            <EmailTemplateSettings surveyType={survey.type} />
          </Route>
          <Route path={`${match.path}delivery/`} exact>
            <div className="mb3">
              {survey.type !== 'event_based_email' && (
                <SendingHistoryContainer surveyToken={survey.token} surveyId={survey.id} />
              )}
            </div>
            <div>
              <EmailSetting survey={survey} />
            </div>
          </Route>
          <Route path={match.path}>
            <Redirect to={`${match.url}/emails/`} />
          </Route>
        </Switch>
      </div>
    )
  }

  if (survey.type === 'intercom') {
    if (!surveyBuilder) {
      return null
    }
    const transformedQuestions = transformResponseToPresentationalState(surveyBuilder)
    const primaryQuestion = findMetricQuestion(transformedQuestions) || transformedQuestions[0]
    const language = languages.results.find((language) => language.id === survey.language)
    const intercomSurveyConfiguration = dynamicConstants.surveyConfigurations.find(
      (helpdesk) => helpdesk.name === 'intercom'
    )
    return (
      <IntercomSurveyPublish
        primaryQuestion={primaryQuestion}
        surveyToken={survey.token}
        surveyId={survey.id}
        language={language}
        intercomSurveyConfiguration={intercomSurveyConfiguration}
        isReverseOrder={Boolean(R.path(['settings', 'setups', 'choiceOrder'], survey))}
      />
    )
  }

  if (survey.type === 'web_embed') {
    return (
      <Segment>
        <WebEmbedSurveyPublish />
      </Segment>
    )
  }

  if (dynamicConstants.embedSurveyTypes.includes(survey.type)) {
    if (!surveyBuilder) {
      return null
    }
    const transformedQuestions = transformResponseToPresentationalState(surveyBuilder)
    const primaryQuestion = findMetricQuestion(transformedQuestions) || transformedQuestions[0]

    const handleChangePlaceHolders = (e) => {
      setGenericEmbedPlaceHoldersEmbedCode({
        ...genericEmbedPlaceHoldersEmbedCode,
        [e.target.name]: e.target.value,
      })
    }

    const handleChangeSetups = (name, value) => {
      setEmbedSurveySetups({
        ...embedSurveySetups,
        [name]: value,
      })
    }
    const showIntegrationConfiguration =
      survey.connector && !R.path(['connector', 'is_hidden'], survey)
    return (
      <SurveyPublishSettingContext.Provider
        value={{
          ...genericEmbedPlaceHoldersEmbedCode,
          ...embedSurveySetups,
          survey: surveyBuilder,
          surveyToken: survey.token,
          oldSurvey: survey,
          handleChangePlaceHolders: handleChangePlaceHolders,
          handleChangeSetups: handleChangeSetups,
          embedIntegrations: dynamicConstants.surveyConfigurations,
        }}
      >
        <ErrorBoundary errorFallback={<ErrorMessage />}>
          <EmbedSurveyV2 primaryQuestionTitle={primaryQuestion.text} surveyType={survey.type} />
          {showIntegrationConfiguration && (
            <TicketIntegrationConfiguration connector={survey.connector} />
          )}
        </ErrorBoundary>
      </SurveyPublishSettingContext.Provider>
    )
  }

  return null
}

function EmailSetting({ survey }) {
  const { data: emailSurveySetting } = useAxios({
    url: `survey/survey/${survey.token}/email-survey-setting/`,
    skip: !survey.token,
  })
  if (survey.type === 'oneoff_email') {
    const oneOffSurveyState = R.prop('delivery_status', emailSurveySetting)
    if (oneOffSurveyState === 'sent') {
      return (
        <Segment>
          <SendAnotherBatch surveyToken={survey.token} oneOffSurveyState={oneOffSurveyState} />
        </Segment>
      )
    }

    if (oneOffSurveyState === 'draft') {
      return (
        <Segment>
          <OneoffEmailDeliverySettingContainer />
        </Segment>
      )
    }
  }

  if (['NPS', 'ongoing_email'].includes(survey.type)) {
    return (
      <Segment>
        <OngoingEmailDeliverySettingContainer />
      </Segment>
    )
  }

  if (survey.type === 'event_based_email') {
    return (
      <Segment>
        <EventBasedEmailDeliverySettingContainer connector={survey.connector} />
      </Segment>
    )
  }

  return null
}

function useSurveyId() {
  const { surveyId } = useParams()
  return surveyId
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/pages/SurveyPublishSettingV2/index.js